import { useEffect, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import useLiterals from 'utils/hooks/useLiterals';
import Debugger from 'utils/debugger';
import { captureMercadoPagoError } from 'utils/sendSentryLog';

/**
 * Extrae creq y external_resource_url según la doc de Mercado Pago (Integrar 3DS con Checkout API).
 * La respuesta del pago incluye three_ds_info con: external_resource_url (URL del Challenge) y creq (identificador).
 * Acepta three_ds_info (snake_case, como en el JSON de la doc) o _three_ds_info / threeDS (compatibilidad).
 */
function getChallengeParams(paymentOrThreeDsInfo) {
  if (!paymentOrThreeDsInfo) return null;
  // Doc: atributo del pago "three_ds_info" con external_resource_url y creq (ejemplo doChallenge usa _three_ds_info)
  const threeDs = paymentOrThreeDsInfo.three_ds_info
    ?? paymentOrThreeDsInfo._three_ds_info // eslint-disable-line no-underscore-dangle -- doc MP
    ?? paymentOrThreeDsInfo.threeDS
    ?? paymentOrThreeDsInfo;
  const creq = threeDs?.creq;
  const externalResourceUrl = threeDs?.external_resource_url ?? threeDs?.challengeUrl;
  if (!externalResourceUrl || !creq || String(creq).trim() === '') return null;
  return {
    creq,
    external_resource_url: externalResourceUrl,
  };
}

/**
 * Challenge 3DS según documentación Mercado Pago: iframe con formulario method post, action = external_resource_url,
 * input oculto creq; después hacer post del form. Evento del iframe: e.data.status === "COMPLETE".
 * Ref: Cómo integrar 3DS con Checkout API.
 */
const MSG_MISSING_CREQ = 'Para completar la autenticación 3DS la respuesta del pago debe incluir el campo creq (en three_ds_info o threeDS). Revisa que tu backend devuelva la respuesta completa de la API de MercadoPago.';

const Challenge3DS = ({
  payment,
  threeDsInfo,
  userInfo,
  paymentId,
  onChallengeComplete,
  onError,
}) => {
  const literals = useLiterals('mercadopago');
  const challengeLiterals = literals.challenge || {};
  const iframeRef = useRef(null);
  const formRef = useRef(null);
  const [challengeCompleted, setChallengeCompleted] = useState(false);

  // Fuente: payment (status, status_detail, three_ds_info) como en la doc, o threeDsInfo directo
  const source = payment ?? threeDsInfo;

  useEffect(() => {
    const sentryContext = {
      feature: 'Challenge3DS',
      userId: userInfo?.id,
      email: userInfo?.email,
      paymentId,
    };
    if (!source) {
      const err = new Error('Información del Challenge 3DS no disponible');
      captureMercadoPagoError(err, { ...sentryContext, step: 'unavailable' });
      if (onError) onError(err);
      return;
    }

    // Doc: cuando el Challenge es necesario, status === "pending" y status_detail === "pending_challenge".
    // No todos los pagos requieren challenge; si ya viene aprobado u otro estado, dejar que el padre consulte el estado.
    if (payment) {
      const status = payment.status;
      const statusDetail = payment.status_detail ?? payment.statusDetail;
      if (status !== 'pending' || statusDetail !== 'pending_challenge') {
        if (onChallengeComplete) onChallengeComplete();
        return;
      }
    }

    // El padre (useMercadoPagoFlow) ya valida creq y URL antes de mostrar el Challenge; aquí solo extraemos params.
    const params = getChallengeParams(source);
    if (!params?.external_resource_url || !params?.creq) {
      const err = new Error(MSG_MISSING_CREQ);
      captureMercadoPagoError(err, { ...sentryContext, step: 'missingCreq' });
      if (onError) onError(err);
      return;
    }

    const iframe = document.createElement('iframe');
    iframe.name = 'challenge-iframe';
    iframe.id = 'challenge-iframe';
    iframeRef.current = iframe;

    const container = document.getElementById('challenge-container');
    if (!container) {
      const err = new Error('Contenedor del Challenge no encontrado');
      captureMercadoPagoError(err, { ...sentryContext, step: 'containerNotFound' });
      if (onError) onError(err);
      return;
    }

    container.appendChild(iframe);

    // Doc: formulario con method post, action = external_resource_url, input oculto con valor creq; hacer post del form.
    // Form en documento padre (target=iframe) para garantizar envío POST; la respuesta se carga en el iframe.
    const { external_resource_url: externalResourceUrl, creq } = params;
    try {
      const form = document.createElement('form');
      form.name = 'challenge-form';
      form.setAttribute('target', 'challenge-iframe');
      form.setAttribute('method', 'post');
      form.setAttribute('action', externalResourceUrl);
      form.method = 'post';
      form.action = externalResourceUrl;

      const hiddenField = document.createElement('input');
      hiddenField.setAttribute('type', 'hidden');
      hiddenField.setAttribute('name', 'creq');
      hiddenField.setAttribute('value', creq);
      form.appendChild(hiddenField);

      // Ocultar el form para que no cubra el iframe ni capture clics/teclado; el POST ya se dispara igual
      form.setAttribute('style', 'position:fixed;left:-9999px;top:0;width:0;height:0;overflow:hidden;pointer-events:none;visibility:hidden;');
      document.body.appendChild(form);
      formRef.current = form;
      form.submit();
    } catch (err) {
      Debugger.error('Error al crear formulario del Challenge:', err);
      const sentryErr = new Error('Error al inicializar el Challenge 3DS');
      captureMercadoPagoError(sentryErr, { ...sentryContext, step: 'initError' });
      if (onError) onError(err);
    }

    // eslint-disable-next-line consistent-return, react-hooks/exhaustive-deps
    return () => {
      if (formRef.current?.parentNode) {
        formRef.current.parentNode.removeChild(formRef.current);
        formRef.current = null;
      }
      if (iframeRef.current?.parentNode) {
        iframeRef.current.parentNode.removeChild(iframeRef.current);
      }
    };
  }, [source, payment, onError, onChallengeComplete, userInfo?.id, userInfo?.email, paymentId]);

  useEffect(() => {
    // Doc: escuchar evento que indica que el Challenge finalizó: e.data.status === "COMPLETE"
    const handleMessage = (event) => {
      if (event.origin === window.location.origin) return;
      if (event.data && event.data.status === 'COMPLETE') {
        setChallengeCompleted(true);
        if (onChallengeComplete) onChallengeComplete();
      }
    };

    window.addEventListener('message', handleMessage);
    return () => window.removeEventListener('message', handleMessage);
  }, [onChallengeComplete]);

  return (
    <div className='challenge-3ds-container'>
      <h4 className='mb-4'>{challengeLiterals.title}</h4>
      <p className='mb-4 text-neutral-500'>
        {challengeLiterals.description}
      </p>
      <div id='challenge-container' className='challenge-iframe-wrapper' />
      {challengeCompleted && (
        <div className='mt-4'>
          <p className='text-success'>{challengeLiterals.verifying}</p>
        </div>
      )}
    </div>
  );
};

Challenge3DS.propTypes = {
  /** Objeto payment según doc MP: status "pending", status_detail "pending_challenge", three_ds_info { creq, external_resource_url } */
  payment: PropTypes.shape({
    status: PropTypes.string,
    status_detail: PropTypes.string,
    statusDetail: PropTypes.string,
    three_ds_info: PropTypes.shape({
      creq: PropTypes.string.isRequired,
      external_resource_url: PropTypes.string.isRequired,
    }),
    // eslint-disable-next-line no-underscore-dangle -- doc ejemplo doChallenge usa _three_ds_info
    _three_ds_info: PropTypes.shape({
      creq: PropTypes.string.isRequired,
      external_resource_url: PropTypes.string.isRequired,
    }),
    threeDS: PropTypes.shape({
      creq: PropTypes.string,
      external_resource_url: PropTypes.string,
      challengeUrl: PropTypes.string,
    }),
  }),
  /** three_ds_info directo (mismo contenido que payment.three_ds_info) para flujo que ya tiene el objeto separado */
  threeDsInfo: PropTypes.shape({
    creq: PropTypes.string,
    external_resource_url: PropTypes.string,
    challengeUrl: PropTypes.string,
  }),
  /** Para Sentry: { id, email } */
  userInfo: PropTypes.shape({
    id: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
    email: PropTypes.string,
  }),
  paymentId: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  onChallengeComplete: PropTypes.func.isRequired,
  onError: PropTypes.func,
};

export default Challenge3DS;
